import React from "react";
import { Prompt } from "react-router";
import { render, unmountComponentAtNode } from "react-dom";
import i18n, { useTranslation } from "utilities/i18n";
import { Location } from "history";
import { CancelButton, SubmitButton } from "elements";

interface BlockingFormProps {
  unsavedChanges: boolean;
  allowedSubRoutes?: string[];
}

export const RouteConfirmation = (
  message: string,
  callback: (result: boolean) => void
) => {
  const container = document.createElement("div");
  container.setAttribute("navigation-confirmation", "");
  document.body.appendChild(container);

  const unmount = () => {
    unmountComponentAtNode(container);
    document.body.removeChild(container);
  };

  const cancelRouteChange = () => {
    unmount();
    callback(false);
  };

  const confirmLeave = () => {
    unmount();
    callback(true);
  };

  render(
    <div className="modal">
      <div className="overlay" onClick={cancelRouteChange} />
      <div
        className="modal-content form grid fixed theme-independent"
        id="confirm-modal"
      >
        <h4>{i18n.t("USER_UNSAVED_CHANGES")}</h4>
        <div className="contents-container">
          <div data-testid="confirm-modal">
            <div className="small-text" data-testid="confirmation">
              {message}
            </div>
            <div className="btn-container">
              <CancelButton
                className="btn theme-independent"
                onClick={cancelRouteChange}
              >
                {i18n.t("CANCEL")}
              </CancelButton>
              <SubmitButton
                isSubmitting={false}
                testId="confirm"
                onClick={confirmLeave}
                labelKey="LEAVE"
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    container
  );
};

const BlockingForm: React.FC<BlockingFormProps> = props => {
  const { unsavedChanges, children, allowedSubRoutes } = props;
  const { t } = useTranslation();

  const confirmRouteChange = (location: Location) => {
    if (
      allowedSubRoutes &&
      allowedSubRoutes.some(subroute => location.pathname.includes(subroute))
    ) {
      return true;
    }
    return t("UNSAVED_CHANGES_WARNING");
  };

  return (
    <>
      <Prompt when={unsavedChanges} message={confirmRouteChange} />
      {children}
    </>
  );
};

export default BlockingForm;
